import i18next from 'i18next';
import moment from 'moment';

import { Charge } from '@r6digital/rapidstor-types/react/entities/Charge';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import { LineItem } from '@r6digital/rapidstor-types/types/Merchandise';
import { CostRetrieveResponse } from '@r6digital/rapidstor-types/types/Reservation';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import {
    CorporationCode,
    getCorpTranslations,
} from '@/core/translations/helpers';
import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';

import { CostRetrieveItem } from '@/core/components/wizard/pricing-table';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';
import { descriptorDimensions } from '@/core/utils/rapidstor';

/**
 * Helper method to check if a unitType has a deal or not
 * @param unitType
 * @returns boolean
 */
export const unitTypeHasDeal = (unitType: UnitType): boolean => {
    if (unitType.deal) {
        return true;
    }
    return false;
};

/**
 * @param descriptor
 *
 * @returns number
 */
export const descriptorDealCount = (descriptor: Descriptor): number => descriptor.deals.length;

/**
 * Timeframe label formatting
 *
 * @param location
 *
 * @returns string
 */
export const formatTimeframe = (location: Location): string => {
    if (location.rapidstor.showWeeklyRates) {
        return i18next.t('time:per_week');
    }
    if (location.settings.billingDefault === '28DayAnniversary') {
        return ' per 4 weeks';
    }
    return i18next.t('time:per_month');
};

/**
 * Size label formatting
 *
 * @param value
 * @param location
 *
 * @returns string
 */
export const formatSize = (
    value: number,
    location: Location,
    noSubscript?: boolean,
    noSymbol?: boolean,
): string => {
    const formatValue = value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        trailingZeroDisplay: 'stripIfInteger',
    });

    if (noSymbol) {
        return formatValue;
    }

    if (noSubscript) {
        return `${formatValue}${location.rapidstor.sizeSymbol}`;
    }

    return `${formatValue}${location.rapidstor.areaSizeSymbol}`;
};

/**
 * Percentage difference for discount display
 *
 * @param original
 * @param discounted
 *
 * @returns string
 */
export const percentageDifference = (
    original: number,
    discounted: number,
): string => {
    const savings = ((original - discounted) / original) * 100;
    return `${savings.toFixed()}%`;
};

/**
 * Rounds a number to a specified number of decimal places
 *
 * @param value
 * @param decimals
 *
 * @returns number
 */
export const numberRound = (value: number, decimals = 2): number => {
    if (!value) {
        return value;
    }
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

/**
 * Method used for determining the sizing label breakpoints
 *
 * @param size
 * @param location
 *
 * @returns string
 */
export const sizeLabel = (size: number, location: Location): string => {
    if (size <= location.rapidstor.smallMediumSizes) {
        return i18next.t('labels:small');
    } if (size <= location.rapidstor.mediumLargeSizes) {
        return i18next.t('labels:medium');
    }
    return i18next.t('labels:large');
};

/**
 * This control the descriptor labels, respecting rapidstor settings to either use descriptorTitle.
 * Or if not selected use either area or dimensions.
 *
 * @param descriptor
 * @param location
 *
 * @returns string
 */
export const descriptorTitle = (
    descriptor: Descriptor,
    location: Location,
): (string | number)[] => {
    if (location.rapidstor.useDescriptorTitle) {
        const corpReplacements = getCorpTranslations(
            location.corpCode as CorporationCode,
        );

        if (!corpReplacements) {
            return [0, `${descriptor.name}`];
        }

        if (corpReplacements && !('replacements' in corpReplacements)) {
            return [0, `${descriptor.name}`];
        }

        if (
            corpReplacements.replacements
            && typeof corpReplacements.replacements === 'object'
            && descriptor.name in corpReplacements.replacements
        ) {
            return [
                0,
                corpReplacements.replacements[
                    descriptor.name as keyof typeof corpReplacements.replacements
                ],
            ];
        }

        return [0, `${descriptor.name}`];
    }
    if (location.settings.unitDimensionDisplay === 'area') {
        return [1, `${formatSize(descriptor.area, location)}`];
    }
    if (descriptor.unitTypes?.length) {
        return [
            2,
            descriptorDimensions(descriptor.unitTypes[0], location),
        ];
    }
    return [0, `${descriptor.name}`];
};

export const unitTypeTitle = (unitType: UnitTypeWithImages, location: Location) => {
    /** Modern unit listing did not display the sizes in the titles but standard did */
    const shouldShowSize = location.rapidstor.features?.['unit-listing'] === 'standard';

    if (location.rapidstor.useDescriptorTitle) {
        const [, title] = descriptorTitle(unitType.descriptor, location);
        if (!shouldShowSize) {
            return title;
        }
        if (location.settings.unitDimensionDisplay === 'area') {
            return `${formatSize(unitType.area, location)} ${title}`;
        }
        return `${descriptorDimensions(unitType, location)} ${title}`;
    }

    if (!shouldShowSize) {
        return unitType.typeName;
    }

    if (location.settings.unitDimensionDisplay === 'area') {
        return `${formatSize(unitType.area, location)} ${unitType.typeName}`;
    }

    return `${descriptorDimensions(unitType, location)} ${unitType.typeName}`;
};

export const descriptorDescriptions = (
    descriptor: Descriptor,
    deal: boolean,
): string[] => {
    const descriptions: string[] = [];

    if (deal && descriptor.unitTypes[0].deal?.descriptions) {
        for (const d of descriptor.unitTypes[0].deal.descriptions) {
            descriptions.push(d);
        }
    } else if (descriptor.descriptions) {
        for (const d of descriptor.descriptions) {
            descriptions.push(d);
        }
    }
    return descriptions.splice(0, 4);
};

/**
 * UnitType sorting helper, supports primary, secondary and tertiary for various levels of sorting
 *
 * @param unitTypes
 * @param location
 *
 * @returns UnitType[]
 */
export const sortUnitTypes = (
    unitTypes: UnitType[],
    location: Location,
): UnitType[] => {
    const array = [...unitTypes];
    const sortOrder = [
        'entryLocation',
        ...location.settings.unitSortOrder,
    ].reverse();
    let sortedArray = array;
    sortOrder.push('none');
    for (const order of sortOrder) {
        sortedArray = sortedArray.sort((a, b) => {
            switch (order) {
                case 'size':
                // Sort by size in ASC order
                    return a.area - b.area;

                case 'floor':
                // Sort by floor in ASC order
                    return a.floor - b.floor;

                case 'price':
                // Sort by rate in ASC order
                    return !a.rates.discounted
                        ? a.rates.discountedRate - b.rates.discountedRate
                        : a.rates.rate - b.rates.rate;

                case 'availability':
                    return b.totalAvailable - a.totalAvailable;

                case 'none': {
                    const aHasUnits = a.totalAvailable > 0;
                    const bHasUnits = b.totalAvailable > 0;
                    if (aHasUnits === bHasUnits) {
                        return 0;
                    }
                    return aHasUnits ? -1 : 1;
                }

                default:
                    return 0;
            }
        });
    }

    sortedArray = sortedArray.filter(
        (unitType, index) => sortedArray.findIndex((u) => u.floor === unitType.floor) === index,
    );
    return sortedArray;
};

/**
 * Returns the first available UnitType based on the location rapidstor sort settings.
 * If there are none available it will return null.
 *
 * @param unitTypes
 * @param location
 *
 * @returns UnitType | null
 */
export const firstAvailableUnitType = (
    unitTypes: UnitType[],
    location: Location,
): UnitType => {
    const sortedUnitTypes = sortUnitTypes(unitTypes, location);
    return sortedUnitTypes[0];
};

export const isUnitTypeAvailable = (unitType: UnitType) => {
    if (!unitType) {
        return false;
    }

    return unitType.hasComputedAvailable;
};

/**
 * Handy helper function that returns a boolean for whether a descriptor has available unitTypes
 *
 * Reads the server's `hasComputedAvailable` verdict, which folds in
 * `minAvailableUnits` whether or not `hideUnitsMinAvailable` is set. Counting
 * vacant units instead would call a descriptor with 2 units left available on a
 * site with a minimum of 3.
 *
 * @param descriptor
 *
 * @returns boolean
 */
export const isDescriptorAvailable = (descriptor: Descriptor) => {
    if (
        !descriptor
    || !descriptor.unitTypes
    || descriptor.unitTypes.length === 0
    ) {
        return false;
    }

    return descriptor.unitTypes.some(isUnitTypeAvailable);
};

// Mirrors the move-in confirmation email's buildDatesForMoveinLine
// (apps/rapidstor-server/core/emailHelpers.js), so the on-screen summary and the
// email agree: "DD/MM - DD/MM" for a period, a single "DD/MM" when the charge
// covers one day (compared as formatted dates, not raw strings, so a same-day
// charge sent as 00:00:00 -> 23:59:59 still collapses to one date).
export const formatChargePeriod = (startDate?: string, endDate?: string): string => {
    if (!startDate) return '';

    const start = moment.parseZone(startDate).format('DD/MM');
    if (!endDate) return start;

    const end = moment.parseZone(endDate).format('DD/MM');
    return start === end ? start : `${start} - ${end}`;
};

export const transformCostRetrieve = (
    charges: Charge[] | undefined,
    costRetrieve: CostRetrieveResponse | undefined,
    cart: LineItem[] | undefined,
    merchandiseDiscount: number,
): CostRetrieveItem[] => {
    const items: CostRetrieveItem[] = [];

    if (charges) {
        charges.forEach((charge) => {
            if (charge.chargeCategory === 'ReservFee') {
                items.push({
                    chargeAmount: charge.price,
                    initialChargeAmount: charge.price,
                    initialTaxAmount: numberRound(
                        charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100),
                    ),
                    initialTotal: numberRound(
                        charge.price
              + charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100),
                    ),
                    chargeDescription: charge.chargeDescription,
                    discountTaxAmount: 0,
                    discountChargeAmount: 0,
                    taxAmount: numberRound(
                        charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100),
                    ),
                    discountAmount: 0,
                    total: numberRound(
                        charge.price
              + charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100),
                    ),
                });
            }
        });
    }

    if (costRetrieve?.costRetrieveFormatted) {
        costRetrieve.costRetrieveFormatted.forEach((charge) => {
            const originalTaxAmount = (charge.dcPriceTax1 || charge.TaxAmount)
        + (charge.dcPriceTax2 || charge.TaxAmount2);
            // TaxAmount will be the discounted tax amount if the charge has a discount. But it will also be the full tax amount
            // if the charge has no discount. Doing it this way could have some issues.
            const discountTaxAmount = charge.TaxAmount + charge.TaxAmount2;
            const originalChargeAmount = charge.ChargeAmountRnd || charge.ChargeAmount;
            // don't use dcDiscount on the charge, as it includes tax and we need the non-tax discount amount
            // we have to do it this way because, in some cases, the API doesn't return the discount charge
            // amount, only the discounted tax amount.
            const discountWithoutTax = charge.dcDiscount
                ? charge.dcDiscount - discountTaxAmount
                : 0;
            const discountChargeAmount = charge.ChargeAmount !== originalChargeAmount
                ? charge.ChargeAmount
                : originalChargeAmount - discountWithoutTax;

            items.push({
                chargeAmount: charge.ChargeAmount,
                initialChargeAmount: numberRound(originalChargeAmount),
                initialTaxAmount: numberRound(originalTaxAmount),
                initialTotal: numberRound(originalChargeAmount + originalTaxAmount),
                chargeDescription: charge.ChargeDescription,
                chargePeriod: formatChargePeriod(charge.StartDate, charge.EndDate),
                taxAmount: numberRound(charge.TaxAmount + charge.TaxAmount2),
                discountChargeAmount: numberRound(discountChargeAmount),
                discountTaxAmount: numberRound(originalTaxAmount - discountTaxAmount),
                discountAmount: numberRound(
                    originalChargeAmount
            - discountChargeAmount
            + (originalTaxAmount - discountTaxAmount),
                ),
                discountName: charge.sPlanName,
                total: charge.dcTotal,
            });
        });
    }

    if (cart) {
        cart.forEach((item) => {
            if (item.quantity > 0) {
                const amount = item.price * item.quantity;
                const tax = item.tax * item.quantity;
                let taxDiscount = 0.0;
                let discountAmount = 0.0;
                let total = 0.0;

                if (merchandiseDiscount > 0) {
                    discountAmount = amount * (merchandiseDiscount / 100);
                    taxDiscount = tax * (merchandiseDiscount / 100);
                }

                total = amount + tax - discountAmount - taxDiscount;

                items.push({
                    chargeAmount: numberRound(item.price * item.quantity),
                    chargeDescription: item.name,
                    initialChargeAmount: numberRound(item.price * item.quantity),
                    initialTaxAmount: numberRound(tax),
                    initialTotal: numberRound(amount + tax),
                    discountTaxAmount: numberRound(taxDiscount),
                    discountChargeAmount: numberRound(
                        item.price * item.quantity - discountAmount,
                    ),
                    taxAmount: numberRound(item.tax * item.quantity),
                    discountAmount: numberRound(discountAmount + taxDiscount),
                    total: numberRound(total),
                });
            }
        });
    }

    return items;
};

const special = [
    'Ground',
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
];
const endings = ['st', 'nd', 'rd', 'th'];

export const stringifyNumber = (n: number) => {
    if (n < 0) return 'Basement';

    if (n < 11) return special[n];

    if (n >= 11 && n < 20) return `${n}th`;

    const secondNumber = String(n).charAt(1);

    if (secondNumber === '1') return `${n}${endings[0]}`;
    if (secondNumber === '2') return `${n}${endings[1]}`;
    if (secondNumber === '3') return `${n}${endings[2]}`;
    return `${n}${endings[3]}`;
};

export const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

export const formatDealCustomText = (
    deal: Deal,
    location: Location,
    text: string | undefined,
) => {
    if (text === undefined) return undefined;
    let textToFormat: string | undefined = stripHtml(text);
    const discountedRate = deal.discountPlan?.dcPCDiscount === 100
        ? 0
        : (deal.rates.discountedRate ?? deal.rates.rate);

    textToFormat = textToFormat?.replace(
        /{{DiscountRate}}/g,
        formatPriceLabel(formatCurrency(discountedRate, location), location, false, true),
    );
    textToFormat = textToFormat?.replace(
        /{{DiscountSaving}}/g,
        formatPriceLabel(formatCurrency(deal.rates.discountSaved ?? 0, location), location, false, true).replace(/-/g, ''),
    );
    textToFormat = textToFormat?.replace(
        /{{PrepaidAmount}}/g,
        formatPriceLabel(formatCurrency(deal.rates.prepaidAmount ?? deal.rates.rate, location), location, false, true),
    );
    textToFormat = textToFormat?.replace(
        /{{PrepaidSaving}}/g,
        formatPriceLabel(formatCurrency(deal.rates.discountSaved ?? 0, location), location, false, true).replace(/-/g, ''),
    );
    textToFormat = textToFormat?.replace(
        /{{StandardRate}}/g,
        formatPriceLabel(formatCurrency(deal.rates.rate, location), location, false, true),
    );
    return textToFormat;
};

interface ErrorResponse {
    reasonCode: string;
    reasonText: string;
    responseCode: string;
}

export const deconstructScaErrorMessage = (
    errorMessage: string,
): ErrorResponse | null => {
    const regex = /Reason Code:\s*(\w+)\s*Reason Text:\s*(.*)/;
    const match = errorMessage.match(regex);

    // This is because the error message doesn't have a code to indentify it.
    const alreadyRentedMessage = 'Unit is already rented or is no longer available';

    if (match) {
        const responseCodeMatch = errorMessage.match(/Response Code:\s*([^ ]+)/);
        const responseCode = responseCodeMatch ? responseCodeMatch[1] : 'Unknown';

        return {
            reasonCode: match[1],
            reasonText: match[2].trim(),
            responseCode,
        };
    }

    if (errorMessage.includes(alreadyRentedMessage.trim())) {
        return {
            reasonCode: '10001',
            reasonText: 'unitNotAvailable',
            responseCode: 'Unknown',
        };
    }

    if (errorMessage.includes('3D Secure authentication failed')) {
        return {
            reasonCode: '50716',
            reasonText: '3DSecureAuthenticationFailed',
            responseCode: 'Unknown',
        };
    }

    return null;
};

/**
 * Used for displaying the insurance premium INCLUDING tax as it doesn't include it by default
 *
 * @param coverages
 * @param selectedCoverage
 * @param taxRate
 *
 * @returns
 */
/**
 * Checks whether an insurance coverage can be offered for a unit, based on its
 * hidden/deleted flags and its minArea/maxArea band. An unassigned minArea or
 * maxArea means no limit in that direction; when no unit area is known the
 * band is not applied.
 *
 * @param coverage
 * @param unitArea
 *
 * @returns
 */
export const isInsuranceCoverageAvailable = (
    coverage: InsuranceCoverage,
    unitArea?: number,
): boolean => {
    if (coverage.hidden || coverage.deleted) {
        return false;
    }

    if (unitArea !== undefined && unitArea !== null) {
        if (coverage.minArea && parseInt(coverage.minArea) > unitArea) {
            return false;
        }
        if (coverage.maxArea && parseInt(coverage.maxArea) < unitArea) {
            return false;
        }
    }
    return true;
};

export const computedInsurancePremium = (
    coverages: InsuranceCoverage[],
    selectedCoverage: number,
    taxRate: number,
): number => {
    if (selectedCoverage === -1) {
        return 0;
    }

    const taxAmount = numberRound(
        coverages[selectedCoverage].dcPremium * (taxRate / 100),
    );
    const premium = numberRound(coverages[selectedCoverage].dcPremium);
    const total = premium + taxAmount;

    return total;
};

/**
 * Resolve a coverage's helpful text for the active language.
 *
 * Fallback chain: exact language key -> base language (`zh-HK` -> `zh`) ->
 * the default `helpfulText` -> `undefined`. Keys are matched case-insensitively
 * because the admin stores lowercase codes (e.g. `zh-hk`) while i18n reports
 * mixed-case codes (e.g. `zh-HK`). Returns `undefined` when nothing is
 * configured so callers can supply their own fallback caption.
 */
export const resolveInsuranceHelpfulText = (
    coverage: InsuranceCoverage | undefined,
    language: string,
): string | undefined => {
    if (!coverage) {
        return undefined;
    }

    const translations = coverage.helpfulTextTranslations ?? {};
    const keys = Object.keys(translations);
    const lang = language.toLowerCase();
    const base = lang.split('-')[0];

    const match = keys.find((key) => key.toLowerCase() === lang)
        ?? keys.find((key) => key.toLowerCase() === base);

    const translated = match ? translations[match] : undefined;
    if (translated?.trim()) {
        return translated;
    }
    if (coverage.helpfulText?.trim()) {
        return coverage.helpfulText;
    }
    return undefined;
};

export const getFloorString = (unitType: UnitType, location: Location) => {
    let result = null;
    const ground = Number(location.settings.groundFloor);
    if (Number(unitType.floor) === ground && !unitType.inside) {
        result = 'Drive Up';
    } else if (Number(unitType.floor) === ground && unitType.inside) {
        result = 'Ground';
    } else if (Number(unitType.floor) > ground) {
        result = 'Upper';
    } else {
        result = unitType.floor;
    }
    return result;
};

export const descriptorTotalAvailable = (descriptor: Descriptor) => {
    const result = descriptor.unitTypes.reduce((acc, unitType) => {
        if (!unitType.totalAvailable) {
            return acc;
        }
        return acc + unitType.totalAvailable;
    }, 0);

    return result;
};

/**
 * Helper function to build the return URL for an R6Pay redirect.
 *
 * @param paymentMethod - The payment method to use
 * @param reservationToken - The reservation token
 * @param type - The type of payment to return to
 * @returns string - The return URL
 */
export const paymentGatewayReturnUrl = (
    paymentMethod: PaymentProcessor,
    reservationToken: string,
    type: 'move-in' | 'reservation-with-fee',
): string => {
    const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}`;

    switch (paymentMethod) {
        /* Paygate uses a POST request to return the result, so we need to use the API functions through cloudflare (excluding the hash) */
        case PaymentProcessor.PAYGATE:
            return `${baseUrl}api/payment-redirect/${reservationToken}/${type}`;
        default:
            return `${baseUrl}#/api/payment-redirect/${reservationToken}/${type}`;
    }
};
